import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { fmtStamp } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const POLICY = 'politica_cancelamento';
const fmtWhen = (iso) => (iso ? fmtStamp(iso) : '');

export default function EmailModelsTab() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState('marcada');
  const [form, setForm] = useState({ subject: '', body: '' });
  const [contact, setContact] = useState('');
  const [preview, setPreview] = useState(null);
  const [variant, setVariant] = useState('presencial');
  const [child, setChild] = useState(false);
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  const formFor = (d, key) => {
    const m = key === POLICY ? d.policy : d.templates.find(t => t.key === key);
    return { subject: m?.subject || '', body: m?.body || '' };
  };
  const initRef = useRef(false);
  const load = useCallback(async () => {
    const { data: d } = await axios.get(`${API}/api/admin/email-models`);
    setData(d);
    setContact(d.settings.reschedule_contact);
    if (!initRef.current) { initRef.current = true; setForm(formFor(d, 'marcada')); }
  }, []);
  useEffect(() => { load().catch(() => toast.error('Erro ao carregar modelos')); }, [load]);

  const current = data && (sel === POLICY ? data.policy : data.templates.find(t => t.key === sel));
  const selectModel = (key) => { setSel(key); setPreview(null); if (data) setForm(formFor(data, key)); };
  const applySaved = (d, key = sel) => { setData(d); setForm(formFor(d, key)); };

  if (!data) return <p className="text-xs text-brand-muted" data-testid="email-models-loading">A carregar...</p>;
  const isPolicy = sel === POLICY;

  const insertVar = (name) => {
    const el = bodyRef.current;
    const pos = el ? el.selectionStart : form.body.length;
    const txt = `{${name}}`;
    setForm({ ...form, body: form.body.slice(0, pos) + txt + form.body.slice(pos) });
  };
  const missing = () => (isPolicy ? [] : (current.essential || []).filter(v => !form.body.includes(`{${v}}`) && !form.subject.includes(`{${v}}`)));
  const call = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); } finally { setBusy(false); }
  };
  const save = () => {
    const miss = missing();
    if (miss.length && !window.confirm(`Faltam variáveis essenciais neste modelo: ${miss.map(v => `{${v}}`).join(', ')}. Guardar mesmo assim?`)) return;
    call(async () => { const { data: d } = await axios.put(`${API}/api/admin/email-models/${sel}`, form); applySaved(d); }, 'Modelo guardado');
  };
  const reset = () => {
    if (!window.confirm('Repor o texto original deste modelo?')) return;
    call(async () => { const { data: d } = await axios.post(`${API}/api/admin/email-models/${sel}/reset`); applySaved(d); }, 'Texto original reposto');
  };
  const doPreview = () => call(async () => { const { data: d } = await axios.post(`${API}/api/admin/email-models/${sel}/preview`, { ...form, variant, child }); setPreview(d); });
  const sendTest = () => call(async () => { const { data: d } = await axios.post(`${API}/api/admin/email-models/${sel}/test`, { ...form, variant, child }); toast.success(`Teste enviado para ${d.sent_to}`); });
  const saveContact = () => call(async () => { const { data: d } = await axios.put(`${API}/api/admin/comms-settings`, { reschedule_contact: contact }); setData(d); }, 'Contacto guardado');

  return (
    <div data-testid="email-models-tab" className="space-y-4">
      <div className="bg-white border border-brand-border rounded-2xl p-4">
        <Label className="text-xs text-brand-muted mb-1 block">Contacto para remarcações (WhatsApp)</Label>
        <div className="flex gap-2">
          <Input value={contact} onChange={e => setContact(e.target.value)} className="rounded-xl border-brand-border w-48" data-testid="reschedule-contact-input" />
          <button onClick={saveContact} disabled={busy} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm" data-testid="reschedule-contact-save">Guardar</button>
        </div>
        <p className="text-[11px] text-brand-muted mt-1">Usado nos emails como {'{contacto_remarcacoes}'}. É o mesmo para as pacientes de todas as nutricionistas.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[...data.templates, { key: POLICY, label: 'Política de cancelamento (bloco)' }].map(t => (
          <button key={t.key} onClick={() => selectModel(t.key)} data-testid={`email-model-${t.key}`}
            className={`px-3 py-2 rounded-xl text-xs font-medium ${sel === t.key ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{t.label}</button>
        ))}
      </div>

      <div className="bg-white border border-brand-border rounded-2xl p-4 space-y-3" data-testid="email-model-editor">
        {!isPolicy && <p className="text-[11px] text-brand-muted">Quando é enviado: {current.trigger}</p>}
        {!isPolicy && (
          <div><Label className="text-xs text-brand-muted mb-1 block">Assunto</Label>
            <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="rounded-xl border-brand-border" data-testid="email-model-subject" /></div>
        )}
        <div><Label className="text-xs text-brand-muted mb-1 block">{isPolicy ? 'Texto da política (usado em todos os modelos onde aparece)' : 'Texto do email (separa os parágrafos com uma linha em branco)'}</Label>
          <Textarea ref={bodyRef} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={isPolicy ? 4 : 14} className="rounded-xl border-brand-border font-mono text-xs" data-testid="email-model-body" /></div>
        <p className="text-[11px] text-brand-muted" data-testid="email-model-format-hint">Formatação: **texto** para negrito e *texto* para itálico.</p>
        {!isPolicy && (
          <div><p className="text-[11px] text-brand-muted mb-1">Variáveis (clica para inserir):</p>
            <div className="flex flex-wrap gap-1">{data.variables.map(v => (
              <button key={v.name} title={v.description} onClick={() => insertVar(v.name)} className="text-[11px] bg-brand-bg border border-brand-border rounded-full px-2 py-0.5 text-brand-text" data-testid={`var-${v.name}`}>{`{${v.name}}`}</button>
            ))}</div></div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <select value={variant} onChange={e => setVariant(e.target.value)} className="text-xs border border-brand-border rounded-lg px-2 py-1.5" data-testid="preview-variant">
            <option value="presencial">Presencial</option><option value="online">Online</option></select>
          <label className="text-xs text-brand-muted flex items-center gap-1"><input type="checkbox" checked={child} onChange={e => setChild(e.target.checked)} data-testid="preview-child" /> Ficha de criança</label>
          <button onClick={doPreview} disabled={busy} className="border border-brand-border rounded-full px-3 py-1.5 text-xs" data-testid="email-model-preview-btn">Pré-visualizar</button>
          <button onClick={sendTest} disabled={busy} className="border border-brand-border rounded-full px-3 py-1.5 text-xs" data-testid="email-model-test-btn">Enviar teste para o meu email</button>
          <button onClick={reset} disabled={busy} className="border border-brand-border rounded-full px-3 py-1.5 text-xs text-brand-muted" data-testid="email-model-reset-btn">Repor texto original</button>
          <button onClick={save} disabled={busy} className="bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium ml-auto" data-testid="email-model-save-btn">Guardar</button>
        </div>
        {current.updated_by_name && <p className="text-[10px] text-brand-placeholder" data-testid="email-model-audit">Última alteração: {current.updated_by_name}, {fmtWhen(current.updated_at)}</p>}
        {preview && (
          <div className="border border-brand-border rounded-xl p-3 bg-brand-bg/40" data-testid="email-model-preview">
            <p className="text-xs text-brand-muted mb-2">Assunto: <span className="text-brand-text" data-testid="email-model-preview-subject">{preview.subject}</span></p>
            <div className="bg-white rounded-lg" dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
        )}
      </div>
    </div>
  );
}
